import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import { getInitialLanguage } from '../utils/languageDetection';
import Footer from '../components/Footer';
import './Contact.css';

export default function Contact({ user, logout }) {
  const navigate = useNavigate();
  const language = user?.language || getInitialLanguage();
  const t = (key) => getTranslation(language, key);
  const isGerman = language === 'de';
  const [formData, setFormData] = useState({
    senderEmail: user?.email || '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.senderEmail.trim()) {
      toast.error(isGerman ? 'Bitte geben Sie Ihre E-Mail-Adresse ein' : 'Please enter your email address');
      return;
    }
    
    if (!formData.message.trim()) {
      toast.error(t('toasts.enterFeedback'));
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/contact/send-feedback`, formData, { headers });
      
      toast.success(t('toasts.feedbackSentSuccess'));
      setFormData({ senderEmail: user?.email || '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.failedToSendFeedback'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <header className="contact-header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src="/storytales-logo.png" 
              alt="StoryTales Logo" 
              className="logo-image"
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="header-actions desktop-only">
            {user ? (
              <>
                <Button 
                  variant="outline"
                  data-testid="dashboard-button"
                  onClick={() => navigate('/dashboard')}
                  className="header-button"
                >
                  Dashboard
                </Button>
                <Button 
                  variant="outline"
                  data-testid="settings-button"
                  onClick={() => navigate('/settings')}
                  className="header-button"
                >
                  {isGerman ? 'Einstellungen' : 'Settings'}
                </Button>
                <Button 
                  variant="outline"
                  data-testid="logout-button"
                  onClick={logout}
                  className="header-button"
                >
                  {isGerman ? 'Abmelden' : 'Logout'}
                </Button>
              </>
            ) : (
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="header-button"
              >
                {isGerman ? 'Anmelden' : 'Login'}
              </Button>
            )}
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="mobile-menu-toggle mobile-only">
            <button 
              className="hamburger-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            {user ? (
              <>
                <button 
                  className="mobile-menu-item"
                  onClick={() => {
                    navigate('/dashboard');
                    setMobileMenuOpen(false);
                  }}
                >
                  Dashboard
                </button>
                <button 
                  className="mobile-menu-item"
                  onClick={() => {
                    navigate('/settings');
                    setMobileMenuOpen(false);
                  }}
                >
                  {isGerman ? 'Einstellungen' : 'Settings'}
                </button>
                <button 
                  className="mobile-menu-item"
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                >
                  {isGerman ? 'Abmelden' : 'Logout'}
                </button>
              </>
            ) : (
              <button 
                className="mobile-menu-item"
                onClick={() => {
                  navigate('/');
                  setMobileMenuOpen(false);
                }}
              >
                {isGerman ? 'Anmelden' : 'Login'}
              </button>
            )}
          </div>
        )}
      </header>

      <div className="contact-content">
        <div className="contact-hero">
          <h1 data-testid="contact-title">
            📧 {isGerman ? 'Kontakt' : 'Contact Us'}
          </h1>
          <p>{isGerman ? 'Wir freuen uns über dein Feedback und deine Vorschläge!' : 'We\'d love to hear your feedback and suggestions!'}</p>
        </div>

        <div className="contact-form-container">
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <Label htmlFor="senderEmail">{isGerman ? 'Deine E-Mail' : 'Your Email'}</Label>
              <Input
                id="senderEmail"
                data-testid="sender-email-input"
                type="email"
                value={formData.senderEmail}
                onChange={(e) => setFormData({...formData, senderEmail: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="message">{isGerman ? 'Deine Nachricht' : 'Your Message'}</Label>
              <Textarea
                id="message"
                data-testid="message-textarea"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder={isGerman ? 'Teile dein Feedback, Vorschläge oder melde Probleme...' : 'Share your feedback, suggestions, or report any issues...'}
                rows={8}
                required
              />
            </div>

            <Button 
              data-testid="send-feedback-button"
              type="submit" 
              className="send-button" 
              disabled={sending}
            >
              {sending ? (
                <>
                  <span className="spinner"></span>
                  {isGerman ? 'Wird gesendet...' : 'Sending...'}
                </>
              ) : (
                isGerman ? 'Feedback senden' : 'Send Feedback'
              )}
            </Button>
          </form>
        </div>
      </div>
      <Footer language={language} />
    </div>
  );
}
